import { useState, useEffect, useCallback } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { User, CreatorProfile } from '@/types';
import { unifiedAuthService } from './unified-auth-service';
import { Permission } from './permission-manager';

/**
 * Unified Auth Hook
 * 
 * Single hook for all auth and profile needs.
 * Provides reactive state updates and permission checking.
 */
export function useUnifiedAuth() {
  // Auth state
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState<User | null>(null);
  const [creatorProfile, setCreatorProfile] = useState<CreatorProfile | null>(null);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Subscribe to auth changes
  useEffect(() => {
    const unsubscribeAuth = unifiedAuthService.onAuthStateChange((newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setIsAuthenticated(!!newSession);
      setIsLoading(unifiedAuthService.isLoading());
    });
    
    const unsubscribeProfile = unifiedAuthService.onProfileChange((newProfile) => {
      setProfile(newProfile);
      setCreatorProfile(unifiedAuthService.getCreatorProfile());
    });
    
    const unsubscribeError = unifiedAuthService.onError((error) => {
      setError(error);
    });
    
    // Set initial state
    setSession(unifiedAuthService.getSession());
    setUser(unifiedAuthService.getUser());
    setIsAuthenticated(unifiedAuthService.isAuthenticated());
    setProfile(unifiedAuthService.getUserProfile());
    setCreatorProfile(unifiedAuthService.getCreatorProfile());
    setIsLoading(unifiedAuthService.isLoading());
    
    return () => {
      unsubscribeAuth();
      unsubscribeProfile();
      unsubscribeError();
    };
  }, []);
  
  // Auth methods
  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      await unifiedAuthService.signIn(email, password);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      await unifiedAuthService.signInWithProvider('google');
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const signUp = useCallback(async (
    email: string, 
    password: string, 
    metadata?: Record<string, any>
  ) => {
    setError(null);
    try {
      await unifiedAuthService.signUp(email, password, metadata);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const signOut = useCallback(async () => {
    setError(null);
    try {
      await unifiedAuthService.signOut();
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const refreshSession = useCallback(async () => {
    setError(null);
    try {
      await unifiedAuthService.refreshSession();
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  // Profile methods
  const refreshProfile = useCallback(async () => {
    setError(null);
    try {
      const newProfile = await unifiedAuthService.getProfile(true);
      return newProfile;
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  const updateProfile = useCallback(async (updates: Partial<User>) => {
    setError(null);
    try {
      const updatedProfile = await unifiedAuthService.updateProfile(updates);
      return updatedProfile;
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, []);
  
  // Permission methods
  const hasPermission = useCallback((permission: Permission): boolean => {
    return unifiedAuthService.hasPermission(permission);
  }, []);
  
  const hasAnyPermission = useCallback((permissions: Permission[]): boolean => {
    return unifiedAuthService.hasAnyPermission(permissions);
  }, []);
  
  const hasAllPermissions = useCallback((permissions: Permission[]): boolean => {
    return unifiedAuthService.hasAllPermissions(permissions);
  }, []);
  
  // Convenience getters
  const isCreator = profile?.role === 'creator' || profile?.role === 'admin';
  const isAdmin = profile?.role === 'admin';
  const userId = user?.id;
  const userEmail = user?.email;
  
  return {
    // Auth state
    session,
    user,
    isAuthenticated,
    
    // Profile state
    profile,
    creatorProfile,
    
    // Convenience
    userId,
    userEmail,
    isCreator,
    isAdmin,
    
    // Loading/error state
    isLoading,
    error,
    
    // Auth methods
    signIn,
    signInWithGoogle,
    signUp,
    signOut,
    refreshSession,
    
    // Profile methods
    refreshProfile,
    updateProfile,
    
    // Permission methods
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}